import { useEffect } from 'react';
import { Keyboard, X } from 'lucide-react';

/**
 * The keyboard reference.
 *
 * Opened with `?`, which is the one shortcut worth guessing. Everything else is listed
 * here rather than only in tooltips, because a shortcut you have to hover to discover
 * is a shortcut nobody uses.
 */
const GROUPS: { title: string; rows: [string, string][] }[] = [
  {
    title: 'Ansicht',
    rows: [
      ['Leertaste halten', 'Vorher — nur Text und Hintergrund, ohne Techniken und Post-FX'],
      ['Alt · Ziehen', 'Bild verschieben'],
      ['Mittlere Maustaste', 'Bild verschieben'],
      ['Scrollrad', 'Zoomen zum Mauszeiger'],
      ['F', 'Einpassen'],
      ['Shift + F', 'Vollbild'],
      ['Tab', 'Panels ein- und ausblenden'],
    ],
  },
  {
    title: 'Arbeiten',
    rows: [
      ['Ctrl/Cmd + Z', 'Rückgängig'],
      ['Ctrl/Cmd + Shift + Z', 'Wiederholen'],
      ['Ctrl/Cmd + E', 'Bild exportieren'],
      ['Ctrl/Cmd + Shift + E', 'Animation exportieren'],
      ['?', 'Diese Übersicht'],
    ],
  },
  {
    title: 'Regler',
    rows: [
      ['Ziehen', 'Wert ändern'],
      ['Shift · Ziehen', 'Zehnmal feiner'],
      ['Alt · Ziehen', 'Zehnmal gröber'],
      ['Doppelklick', 'Auf den Ausgangswert zurücksetzen'],
      ['Scrollrad über dem Regler', 'Schrittweise ändern'],
    ],
  },
  {
    title: 'Evolution',
    rows: [
      ['Klick auf ein Bild', 'Als Elternteil wählen'],
      ['Enter', 'Nächste Generation züchten'],
    ],
  },
];

export function ShortcutsDialog({ onClose }: { onClose: () => void }): JSX.Element {
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-6" onClick={onClose}>
      <div
        className="max-h-full w-[560px] overflow-y-auto rounded-xl border border-ink-700 bg-ink-900 shadow-2xl shadow-black/70"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 flex items-center gap-2 border-b border-ink-800 bg-ink-900 px-4 py-3">
          <Keyboard size={13} className="text-accent-500" />
          <span className="flex-1 text-[13px] font-medium text-ink-100">Tastatur</span>
          <button type="button" className="ghost-button h-6 w-6 p-0" onClick={onClose}>
            <X size={13} />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-x-6 gap-y-4 px-4 py-4">
          {GROUPS.map((group) => (
            <section key={group.title}>
              <div className="panel-label mb-1.5">{group.title}</div>
              <dl className="space-y-1">
                {group.rows.map(([keys, what]) => (
                  <div key={keys} className="flex items-baseline gap-2">
                    <dt className="shrink-0 rounded bg-ink-850 px-1.5 py-0.5 font-mono text-[10px] text-ink-200">
                      {keys}
                    </dt>
                    <dd className="min-w-0 text-[10px] leading-relaxed text-ink-500">{what}</dd>
                  </div>
                ))}
              </dl>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
